<template>
  <tr class="email-row-component" v-bind:class="{selected: selected}" v-on:click="onSelectionChanged">
    <td>
      <div v-if="!editing">
        <a-popover placement="top">
          <template slot="title">
            <span>Onderwerp: {{ email.subject }}</span>
          </template>
          <template slot="content" v-if="attachments">
            <li v-for="(attachment, index) in attachments.slice(0, 4)" :key="index">
              <file-type-icon :filename="attachment.filename"/>
              {{ attachment.filename }}
            </li>
            <li v-if="attachments.length > 4"><i>...en nog {{ attachments.length - 4 }} andere bijlagen.</i></li>
          </template>

          <a :href="emailShowUri" v-on:click.stop>{{ email.name }} </a>

          <attachment-icon v-if="email.has_attachment" style="cursor: pointer;"/>

          <span v-if="email.sent === 1 && !showColumnTo"> (Verstuurd)</span>
        </a-popover>

        <pencil-icon style="cursor: pointer;" v-on:click.stop="onEditClick"></pencil-icon>
        <breadcrumbs v-if="hasCustomer" :attachableClass="email.morph_class"
                     :attachableId="email.id"></breadcrumbs>
        <br/>

      </div>
      <div v-if="editing">
        <a-input-group compact>
          <a-input ref="emailNameInput" :defaultValue="email.name" style="width: 80%;"
                   v-bind:disabled="updateNameLoading"
                   v-on:click.stop
                   v-on:keyup.enter="onSave" v-on:keyup.esc="onCancel"></a-input>
          <a-button :loading="updateNameLoading" v-on:click.stop="onSave">
            <content-save-icon></content-save-icon>
          </a-button>
        </a-input-group>
      </div>
      <small v-if="email.sent === 1 && !showColumnTo">Aan: {{ email.to }}</small>
      <small v-if="!showColumnFrom">Van: {{ email.from }}</small>
    </td>

    <td v-if="showColumnFrom"> {{ email.from }}</td>

    <td v-if="showColumnTo"> {{ email.to }}</td>

    <td>
      <UserBadgeComponent v-if="hasUser" :user="email.user.data"></UserBadgeComponent>
    </td>
    <td>
      <BadgeComponent v-if="hasCustomer" :customer="email.customer.data"></BadgeComponent>
    </td>
    <td>
      {{ date }}
    </td>
    <td class="compact text-right">
      <a-dropdown :trigger="['click']" class="actions" placement="bottomRight" v-on:click.stop>
        <a-button type="default" @click.stop="e => e.preventDefault()">
          Acties
          <a-icon type="down"/>
        </a-button>

        <a-menu slot="overlay">
          <a-menu-item key="download" class="action-download" @click="onShowClick">
            <eye-icon/>
            Bekijken
          </a-menu-item>
          <a-menu-item key="copy" class="action-copy" @click="onAnswerClick">
            <arrow-left-icon/>
            Beantwoorden
          </a-menu-item>
          <a-menu-item key="move" class="action-move" @click="onForwardClick">
            <arrow-right-icon/>
            Doorsturen
          </a-menu-item>
          <a-menu-item v-if="hasCustomer" key="publish-toggle" :loading="unmatchLoading"
                       class="action-publish-toggle" @click="onUnmatchClick">
            <link-variant-off-icon></link-variant-off-icon>
            Ontkoppelen
          </a-menu-item>
          <a-menu-item v-if="!hasCustomer" key="publish-toggle" :loading="unmatchLoading"
                       class="action-publish-toggle" @click="onMatchClick">
            <link-variant-icon></link-variant-icon>
            Koppelen
          </a-menu-item>

          <a-menu-item v-if="canDeleteEmail" @click="() => {deleteModalVisible = true}"
                       key="delete-task" class="action-delete">
            <span style="color: darkred;">
              <delete-icon></delete-icon>
              Verwijderen
            </span>
          </a-menu-item>
        </a-menu>
      </a-dropdown>

      <delete-modal
          :visible="deleteModalVisible"
          :email="email"
          :description="`Weet je zeker dat je de email (${email.name || ''}) wil verwijderen?`"
          @cancel="() => {deleteModalVisible = false}"
          @deleted="afterDelete"
      ></delete-modal>
    </td>
  </tr>
</template>

<script>
import {mapGetters} from "vuex";
import store from "../store";
import emailApi from "~/api/email";
import BadgeComponent from "~/modules/customer/components/BadgeComponent";
import UserBadgeComponent from "~/modules/user/components/BadgeComponent";
import EmailStatusComponent from "~/modules/email/components/EmailStatusComponent";
import DateFormatEnum from '~/enums/DateFormatEnum';
import FileTypeIcon from '~/components/FileTypeIconComponent';
import DeleteModal from "~/pages/email/components/dialog_modal/DeleteModal.vue";

const moment = require("moment");

export default {
  name: "RowComponent",
  props: {
    email: {
      type: Object,
      required: true
    },
    processable: {
      type: Boolean,
      required: false,
      default: false
    },
    showColumnFrom: {
      type: Boolean,
      default: false,
    },
    showColumnTo: {
      type: Boolean,
      default: false,
    }
  },
  components: {
    DeleteModal,
    BadgeComponent,
    UserBadgeComponent,
    EmailStatusComponent,
    FileTypeIcon
  },
  data() {
    return {
      editing: false,
      updateNameLoading: false,
      processLoading: false,
      unmatchLoading: false,
      deleteModalVisible: false,
    };
  },
  computed: {
    ...mapGetters({
      name: "$_email/name",
    }),
    emailShowUri() {
      if (this.hasCustomer) {
        return "/customer/" + this.email.customer_id + "/email/show/" + this.email.id;
      }
      return "/email/show/" + this.email.id;
    },
    selected() {
      return store.state.selectedEmails.findIndex(x => x.id === this.email.id) >= 0;
    },
    hasCustomer() {
      return typeof this.email.customer_id !== "undefined"
          && this.email.customer_id !== null
          && typeof this.email.customer !== "undefined"
          && this.email.customer !== null;
    },
    hasUser() {
      return typeof this.email.user_id !== "undefined"
          && this.email.user_id !== null
          && typeof this.email.customer !== "undefined"
          && this.email.customer !== null;
    },
    attachments() {
      if (this.email.has_attachment && this.email.data !== null && typeof this.email.data !== "undefined") {
        if (this.email.data.attachments !== null && typeof this.email.data !== "undefined") {
          return this.email.data.attachments;
        }
      }
    },
    date() {
      return moment(this.email.date, moment.ISO_8601).format(DateFormatEnum.DISPLAY_DATETIME);
    },
    canDeleteEmail() {
      return this.$store.state.permissions.delete_email === true;
    },
  },
  created() {
    const STORE_KEY = "$_email";
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store);
    }
  },
  methods: {
    onUnmatchClick() {
      this.unmatchLoading = true;
      emailApi.unmatchEmail(this.email.id)
          .then(() => {
            this.email.customer_id = null;
            this.unmatchLoading = false;
            this.$emit("onUnmatched", this.email);
          })
          .catch(() => {
            this.$notification["error"]({
              message: "Er ging iets mis",
              description: "Kon de e-mail niet ontkoppelen",
            });
          });
    },
    onShowClick() {
      window.location = this.emailShowUri;
    },
    onAnswerClick() {
      window.location = this.hasCustomer
          ? `/customer/${this.email.customer_id}/email/reply/${this.email.id}`
          : `/email/reply/${this.email.id}`;
    },
    onForwardClick() {
      window.location = this.hasCustomer
          ? `/customer/${this.email.customer_id}/email/forward/${this.email.id}`
          : `/email/forward/${this.email.id}`;
    },
    processBadges(badge, count) {
      let $badge = $(badge);
      if (count === 0) {
        $badge.addClass("hidden");
      } else {
        $badge.removeClass("hidden")
            .text(count);
      }
    },
    onProcessClick() {
      this.processLoading = true;

      emailApi.processEmail(this.email.id)
          .then((response) => {
            this.$emit("onProcessed", this.email);
            // JS instead of component
            response.data.emailAccounts.forEach((emailAccount) => {
              this.processBadges(".badge.email-account-count[data-id=\"" + emailAccount.email_account_hashid + "\"]", emailAccount.count);
            });
            // Total
            this.processBadges(".emails-panel-count", response.meta.count);
            this.processBadges(".total-email-count", response.meta.count);

            if (this.hasCustomer) {
              this.navigator.openPath("/customer/" + this.email.customer_id + "/task/create/email/" + this.email.id);
            }

          })
          .catch(() => {
            this.$notification["error"]({
              message: "Er ging iets mis",
              description: "Kon de e-mail niet verwerken",
            });
          });
    },
    onMatchClick() {
      window.location.href = "/email/unmatched/" + this.email.id;
    },
    afterDelete() {
      this.deleteModalVisible = false;
      this.$emit("onDeleted", this.email);
    },
    onEditClick(e) {
      this.editing = true;
    },
    onSave() {
      this.updateEmailName();
    },
    onCancel() {
      this.editing = false;
      this.$refs.emailNameInput.stateValue = this.email.name;
    },
    updateEmailName() {
      if (this.email.name === this.$refs.emailNameInput.stateValue) {
        this.editing = false;
        return;
      }

      this.email.name = this.$refs.emailNameInput.stateValue;
      this.updateNameLoading = true;

      emailApi.updateEmail(this.email.id, {
        name: this.email.name,
      })
          .then((response) => {
            this.editing = false;
            this.updateNameLoading = false;
          })
          .catch((error) => {
            this.$notification["error"]({
              message: "Er ging iets mis",
              description: "Kon de gewijzigde naam van de e-mail niet opslaan",
            });
          });
    },
    onSelectionChanged(e) {
      let shiftKey = false;

      if (typeof e.nativeEvent !== "undefined") {
        shiftKey = e.nativeEvent.shiftKey;
      }

      this.$emit("onSelectionChanged", this.email, !this.selected, shiftKey);
    },
  }
};
</script>

<style scoped>
tr td.half-padding {
  padding: 4px;
}

tr.selected {
  background-color: #1890ff1c;
}

table th.compact, table td.compact {
  white-space: nowrap;
  width: 1px !important;
}

tr .pencil-icon {
  display: none;
}

tr:hover .pencil-icon {
  display: initial;
}
</style>
